import * as Sentry from '@sentry/browser';
import type { FC } from 'react';
import { useEffect } from 'react';
import { Navigate, useSearchParams } from 'react-router';

import AccessDeniedPage from './AccessDeniedPage';
import ErrorPage from './ErrorPage';
import OrgNotFound from './OrgNotFoundPage';
import TokenExpiredPage from './TokenExpiredPage';
import UserNotFoundPage from './UserNotFoundPage';

const Page: FC = () => {
  const [searchParams] = useSearchParams();

  // See https://auth0.com/docs/libraries/common-auth0-library-authentication-errors
  const errorCode = searchParams.get('error-code');
  const errorDescription = searchParams.get('error_description');

  const isOrgNotFound =
    errorCode === 'invalid_request' &&
    (errorDescription ===
      'parameter organization is required for this client' ||
      errorDescription ===
        'authorization request parameter organization must be an organization id');

  const isInvitationUsed =
    errorCode === 'invalid_request' &&
    errorDescription === 'invitation not found or already used';

  const isUserNotFound =
    errorCode === 'access_denied' &&
    !!errorDescription?.includes('not part of the org');

  // The user authenticated but isn't a member of any organisation — a
  // provisioning problem to take up with an administrator, not an app fault.
  const isNoOrgMembership =
    errorCode === 'invalid_request' &&
    !!errorDescription?.includes('does not belong to any organization');

  const isTokenExpired = errorDescription === 'failed to obtain access token';

  const isAccessDenied = errorCode === 'access_denied';

  const isUnhandled =
    !isOrgNotFound &&
    !isInvitationUsed &&
    !isUserNotFound &&
    !isNoOrgMembership &&
    !isTokenExpired &&
    !isAccessDenied;

  useEffect(() => {
    if (isUnhandled) {
      Sentry.captureMessage('Auth failed', {
        contexts: { 'Failure Detail': { errorCode, errorDescription } },
      });
    }
  }, [isUnhandled, errorCode, errorDescription]);

  if (isOrgNotFound) {
    return <OrgNotFound />;
  }

  if (isInvitationUsed) {
    return <Navigate to={'/'} />;
  }

  if (isUserNotFound || isNoOrgMembership) {
    return <UserNotFoundPage />;
  }

  if (isTokenExpired) {
    return <TokenExpiredPage />;
  }

  if (isAccessDenied) {
    return <AccessDeniedPage />;
  }

  return <ErrorPage />;
};

export default Page;
